import {parseDecimal} from '../../../../utils/index.js';
import {consolidarOcupacoes, rendasDoMembro, somarRendas} from '../../../publica/Page/inscricao/rendaMembro.js';
import {encontrarIndiceMembro} from '../../../publica/Page/inscricao/membroFamiliar.js';

/*
 * Conversão entre o InscricaoAdministrativaVO (GET/POST/PUT /rs/inscricoes) e
 * os valores do formulário de criar/editar inscrição do administrativo.
 * Os formatos das sub-listas e das datas (dd/MM/yyyy) seguem o InscricaoVO do
 * fluxo público (ver publica/Page/inscricao/inscricaoPayload.js).
 */

export const PROGRAMA = Object.freeze({
    INGRESSANTE: 1,
    VETERANO: 2,
    PROUNI: 3,
    FIES: 4,
    INGRESSANTE_COLEGIO_TECNOLOGICO: 5,
    STRICTU_SENSU: 6
});

export const TIPOS_RESPONSAVEL = Object.freeze([
    {valor: 'NAO_TEM', descricao: 'Não tem'},
    {valor: 'EMANCIPADO', descricao: 'Emancipado'},
    {valor: 'PAI', descricao: 'Pai'},
    {valor: 'MAE', descricao: 'Mãe'},
    {valor: 'TUTOR', descricao: 'Tutor'}
]);

export const CATEGORIAS = Object.freeze(['Docente', 'Técnico-Administrativo']);

const TIPOS_COM_RESPONSAVEL = ['PAI', 'MAE', 'TUTOR'];

export const exigeDadosResponsavel = tipo => TIPOS_COM_RESPONSAVEL.includes(tipo);

/* O que aparece em cada programa, igual ao _form.xhtml do PrimeFaces. */
export const regrasDoPrograma = (programaId, processo = {}) => {
    const programa = Number(programaId) || null;
    const veterano = programa === PROGRAMA.VETERANO;
    const colegio = programa === PROGRAMA.INGRESSANTE_COLEGIO_TECNOLOGICO;

    return {
        programa,
        exibirCodigoAluno: veterano,
        exibirVaga: !veterano,
        exibirPsInscricao: !veterano && !colegio,
        exibirCategoria: !!processo?.exigirCategoria && !veterano && !colegio,
        exibirCotas: !veterano && !colegio,
        exibirEnem: !veterano && !colegio,
        exibirVeterano: veterano,
        exibirDisponibilidade: !veterano,
        exibirCadUnico: colegio,
        exibirDivida: veterano
    };
};

const somenteDigitos = valor => String(valor ?? '').replace(/\D/g, '');

/* Data em dd/MM/yyyy existente no calendário. */
export const dataBrValida = valor => {
    const texto = String(valor ?? '');
    const partes = texto.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!partes) return false;
    const dia = Number(partes[1]);
    const mes = Number(partes[2]);
    const ano = Number(partes[3]);
    const data = new Date(ano, mes - 1, dia);
    return data.getFullYear() === ano && data.getMonth() === mes - 1 && data.getDate() === dia;
};

/* Idade em anos completos numa data de referência; null quando a data de nascimento é inválida. */
export const calcularIdadeEmAnos = (dataNascimento, hoje = new Date()) => {
    if (!dataBrValida(dataNascimento)) return null;
    const [dia, mes, ano] = dataNascimento.split('/').map(Number);
    let idade = hoje.getFullYear() - ano;
    const mesAtual = hoje.getMonth() + 1;
    if (mesAtual < mes || (mesAtual === mes && hoje.getDate() < dia)) {
        idade--;
    }
    return idade;
};

export const ehMenorDeIdade = (dataNascimento, hoje) => {
    const idade = calcularIdadeEmAnos(dataNascimento, hoje);
    return idade !== null && idade < 18;
};

/*
 * O bloco do responsável legal aparece quando a data de nascimento é válida e
 * o inscrito é menor de idade ou já tem responsável cadastrado.
 */
export const exibirResponsavelLegal = (dataNascimento, temResponsavelCadastrado, hoje) =>
    dataBrValida(dataNascimento) && (ehMenorDeIdade(dataNascimento, hoje) || !!temResponsavelCadastrado);

export const formatarCpf = valor => {
    const digitos = somenteDigitos(valor);
    if (digitos.length !== 11) return valor ? String(valor) : '';
    return `${digitos.slice(0, 3)}.${digitos.slice(3, 6)}.${digitos.slice(6, 9)}-${digitos.slice(9)}`;
};

export const formatarCep = valor => {
    const digitos = somenteDigitos(valor);
    if (!digitos) return '';
    const completo = digitos.padStart(8, '0');
    return `${completo.slice(0, 5)}-${completo.slice(5)}`;
};

/* DDD + número no formato das máscaras de telefone e celular do formulário. */
export const formatarTelefone = (ddd, numero) => {
    const digitos = somenteDigitos(`${ddd ?? ''}${numero ?? ''}`);
    if (digitos.length === 10) return `(${digitos.slice(0, 2)}) ${digitos.slice(2, 6)}-${digitos.slice(6)}`;
    if (digitos.length === 11) return `(${digitos.slice(0, 2)}) ${digitos.slice(2, 7)}-${digitos.slice(7)}`;
    return digitos;
};

const separarTelefone = valor => {
    const digitos = somenteDigitos(valor);
    if (digitos.length <= 2) return {ddd: null, numero: digitos || null};
    return {ddd: Number(digitos.slice(0, 2)), numero: digitos.slice(2)};
};

const booleanoParaTexto = valor => (valor === true ? 'true' : valor === false ? 'false' : '');
const textoParaBooleano = valor => (valor === 'true' || valor === true ? true : valor === 'false' || valor === false ? false : null);
const idOuVazio = objeto => (objeto && objeto.id !== null && objeto.id !== undefined ? String(objeto.id) : '');
const referencia = id => (id === '' || id === null || id === undefined ? null : {id: Number(id)});
const numeroOuVazio = valor => (valor === null || valor === undefined || valor === '' ? '' : Number(valor));
const textoOuNulo = valor => (valor === null || valor === undefined || String(valor).trim() === '' ? null : String(valor).trim());

const enderecoVazio = () => ({logradouro: '', numero: '', complemento: '', bairro: '', cep: '', cidade: null});

const ehResidencial = endereco => endereco?.id?.tipoEndereco === 'RESIDENCIAL';

/* Endereço residencial da pessoa (o mesmo que o JSF usa), ou o primeiro da lista. */
export const enderecoResidencial = (enderecos = []) =>
    (enderecos || []).find(ehResidencial) || (enderecos || [])[0] || null;

const cidadeDoEndereco = cidade => (cidade && cidade.id ? {
    id: cidade.id,
    nome: cidade.nome || '',
    uf: {uf: cidade.uf?.uf || '', nome: cidade.uf?.nome || ''}
} : null);

const enderecoParaFormulario = endereco => (endereco ? {
    logradouro: endereco.logradouro || '',
    numero: endereco.numero || '',
    complemento: endereco.complemento || '',
    bairro: endereco.bairro || '',
    cep: formatarCep(endereco.cep),
    cidade: cidadeDoEndereco(endereco.cidade)
} : enderecoVazio());

export const descricaoCidade = cidade => {
    if (!cidade || !cidade.nome) return '';
    const uf = cidade.uf?.uf || cidade.uf?.nome || '';
    return uf ? `${cidade.nome} - ${uf}` : cidade.nome;
};

/* Pontuação do ENEM com vírgula (máscara 999,9999 do JSF). */
const pontuacaoParaFormulario = valor => (valor === null || valor === undefined || valor === '' ? '' : String(valor).replace('.', ','));

export const estadoInicial = () => ({
    id: null,
    idProcessoSeletivo: '',
    codigoAluno: '',
    pessoa: {
        id: null,
        cpf: '',
        nome: '',
        habilitarNomeSocial: false,
        nomeSocial: '',
        dataNascimento: '',
        sexo: '',
        nomeMae: '',
        nomePai: '',
        estadoCivil: '',
        raca: '',
        nacionalidade: '',
        rg: '',
        orgaoRg: '',
        deficiencias: [],
        dataEmancipacao: '',
        emailAtual: '',
        telefone: '',
        celular: '',
        celularDois: ''
    },
    endereco: enderecoVazio(),
    tipoResponsavel: '',
    responsavel: {
        nome: '',
        cpf: '',
        rg: '',
        dataNascimento: '',
        emailAtual: '',
        celular: '',
        endereco: enderecoVazio()
    },
    tipoHabitacao: '',
    tipoMoradia: '',
    parentesBeneficiarios: [],
    renda: [],
    disponibilidade: '',
    vlrPensao: '',
    vlrPensaoFilho: '',
    declaracaoIR: '',
    cadUnicoOuTransferenciaRenda: '',
    enderecoComposicaoFamiliares: [],
    composicaoFamiliar: [],
    vaga: '',
    psInscricao: '',
    categoria: '',
    sistemaCotas: '',
    utilizarEnem: '',
    inscricaoEnem: {
        id: null,
        processoSeletivoEnem: '',
        cdEnem: '',
        pontuacao: '',
        nomeArquivo: '',
        mimetype: '',
        arquivo: null
    },
    inscricaoVeterano: {
        possuiEnem: '',
        possuiBeneficioNaIes: '',
        descricaoBeneficio: '',
        participouProcessoAnterior: '',
        descricaoProcessoAnterior: '',
        divida: ''
    },
    inscricaoEnsino: [],
    motivo: '',
    tipoBolsa: '',
    percentualBolsa: '',
    observacao: '',
    inscricaoAnexos: [],
    calendarios: [],
    idAtendente: ''
});

/* Tipo de responsável lido do VO: o campo próprio, ou deduzido da pessoa (emancipada / responsável atual). */
const tipoResponsavelDoVo = vo => {
    if (vo.tipoResponsavel) return vo.tipoResponsavel;
    const pessoa = vo.pessoa || {};
    if (pessoa.emancipada === 'SIM') return 'EMANCIPADO';
    return pessoa.responsavelAtual?.tipoResponsavel || '';
};

/* Valores do formulário a partir do InscricaoAdministrativaVO (edição ou pré-preenchimento). */
export const montarEstadoInicial = (vo = {}) => {
    const base = estadoInicial();
    if (!vo) return base;

    const pessoa = vo.pessoa || {};
    const endereco = enderecoResidencial(pessoa.enderecos);
    const responsavel = pessoa.responsavelAtual?.responsavel || null;
    const enderecoResponsavel = responsavel ? enderecoResidencial(responsavel.enderecos) : null;
    const enem = vo.inscricaoEnem || null;
    const veterano = vo.inscricaoVeterano || {};

    return {
        ...base,
        id: vo.id ?? null,
        idProcessoSeletivo: idOuVazio(vo.processoSeletivo),
        codigoAluno: vo.codigoAluno === null || vo.codigoAluno === undefined ? '' : String(vo.codigoAluno),
        pessoa: {
            ...base.pessoa,
            id: pessoa.id ?? null,
            cpf: formatarCpf(pessoa.cpf),
            nome: pessoa.nome || '',
            habilitarNomeSocial: !!pessoa.nomeSocial,
            nomeSocial: pessoa.nomeSocial || '',
            dataNascimento: pessoa.dataNascimento || '',
            sexo: pessoa.sexo || '',
            nomeMae: pessoa.nomeMae || '',
            nomePai: pessoa.nomePai || '',
            estadoCivil: idOuVazio(pessoa.estadoCivil),
            raca: idOuVazio(pessoa.raca),
            nacionalidade: idOuVazio(pessoa.nacionalidade),
            rg: pessoa.rg || '',
            orgaoRg: pessoa.orgaoRg || '',
            deficiencias: (pessoa.deficiencias || [])
                .map(item => item?.deficiencia?.id)
                .filter(idDeficiencia => idDeficiencia !== null && idDeficiencia !== undefined)
                .map(String),
            dataEmancipacao: pessoa.dataEmancipacao || '',
            emailAtual: pessoa.emailAtual || '',
            telefone: endereco ? formatarTelefone(endereco.dddTelefonePessoa, endereco.telefonePessoa) : '',
            celular: endereco ? formatarTelefone(endereco.dddCelularPessoa, endereco.celularPessoa) : '',
            celularDois: endereco ? formatarTelefone(endereco.dddCelularPessoaDois, endereco.celularPessoaDois) : ''
        },
        endereco: enderecoParaFormulario(endereco),
        tipoResponsavel: tipoResponsavelDoVo(vo),
        responsavel: {
            nome: responsavel?.nome || '',
            cpf: formatarCpf(responsavel?.cpf),
            rg: responsavel?.rg || '',
            dataNascimento: responsavel?.dataNascimento || '',
            emailAtual: responsavel?.emailAtual || '',
            celular: enderecoResponsavel ? formatarTelefone(enderecoResponsavel.dddCelularPessoa, enderecoResponsavel.celularPessoa) : '',
            endereco: enderecoParaFormulario(enderecoResponsavel)
        },
        tipoHabitacao: idOuVazio(vo.tipoHabitacao),
        tipoMoradia: idOuVazio(vo.tipoMoradia),
        parentesBeneficiarios: vo.parentesBeneficiarios || [],
        renda: (vo.renda || []).map(item => ({...item, vlrRenda: Number(item.vlrRenda) || 0})),
        disponibilidade: numeroOuVazio(vo.disponibilidade),
        vlrPensao: numeroOuVazio(vo.vlrPensao),
        vlrPensaoFilho: numeroOuVazio(vo.vlrPensaoFilho),
        declaracaoIR: vo.declaracaoIR || '',
        cadUnicoOuTransferenciaRenda: booleanoParaTexto(vo.cadUnicoOuTransferenciaRenda),
        enderecoComposicaoFamiliares: (vo.enderecoComposicaoFamiliares || []).map(item => ({
            ...item,
            cep: formatarCep(item.cep),
            uf: item.uf || item.cidade?.uf?.uf || ''
        })),
        composicaoFamiliar: vo.composicaoFamiliar || [],
        vaga: idOuVazio(vo.vaga),
        psInscricao: idOuVazio(vo.psInscricao),
        categoria: vo.categoria || '',
        sistemaCotas: booleanoParaTexto(vo.sistemaCotas),
        utilizarEnem: vo.utilizacaoNota === 'E' ? 'true' : (vo.utilizacaoNota ? 'false' : ''),
        inscricaoEnem: {
            id: enem?.id ?? null,
            processoSeletivoEnem: idOuVazio(enem?.processoSeletivoEnem),
            cdEnem: enem?.cdEnem === null || enem?.cdEnem === undefined ? '' : String(enem.cdEnem),
            pontuacao: pontuacaoParaFormulario(enem?.pontuacao),
            nomeArquivo: enem?.nomeArquivo || '',
            mimetype: enem?.mimetype || '',
            arquivo: null
        },
        inscricaoVeterano: {
            possuiEnem: booleanoParaTexto(veterano.possuiEnem),
            possuiBeneficioNaIes: booleanoParaTexto(veterano.possuiBeneficioNaIes),
            descricaoBeneficio: veterano.descricaoBeneficio || '',
            participouProcessoAnterior: booleanoParaTexto(veterano.participouProcessoAnterior),
            descricaoProcessoAnterior: veterano.descricaoProcessoAnterior || '',
            divida: veterano.divida || ''
        },
        inscricaoEnsino: vo.inscricaoEnsino || [],
        motivo: vo.motivo || '',
        tipoBolsa: vo.tipoBolsa || '',
        percentualBolsa: vo.percentualBolsa === null || vo.percentualBolsa === undefined ? '' : String(vo.percentualBolsa),
        observacao: vo.observacao || '',
        inscricaoAnexos: vo.inscricaoAnexos || [],
        calendarios: vo.calendarios || [],
        idAtendente: vo.idAtendente === null || vo.idAtendente === undefined ? '' : String(vo.idAtendente)
    };
};

/* EnderecoVO da pessoa a partir do endereço do formulário, mantendo o id e os campos não editados do original. */
const montarEnderecoPessoa = (enderecoFormulario, original, telefones = {}) => {
    const anterior = original || {};
    const telefone = separarTelefone(telefones.telefone);
    const celular = separarTelefone(telefones.celular);
    const celularDois = separarTelefone(telefones.celularDois);
    const cep = somenteDigitos(enderecoFormulario?.cep);

    return {
        ...anterior,
        id: {
            idPessoa: anterior.id?.idPessoa ?? null,
            tipoEndereco: anterior.id?.tipoEndereco || 'RESIDENCIAL'
        },
        logradouro: enderecoFormulario?.logradouro || '',
        numero: enderecoFormulario?.numero || '',
        complemento: enderecoFormulario?.complemento || '',
        bairro: enderecoFormulario?.bairro || '',
        cep: cep ? Number(cep) : null,
        cidade: enderecoFormulario?.cidade?.id ? {id: Number(enderecoFormulario.cidade.id)} : null,
        enviaCorrespondencia: anterior.enviaCorrespondencia || 'SIM',
        statusRegistro: anterior.statusRegistro || 'ATIVO',
        dddTelefonePessoa: 'telefone' in telefones ? telefone.ddd : (anterior.dddTelefonePessoa ?? null),
        telefonePessoa: 'telefone' in telefones ? telefone.numero : (anterior.telefonePessoa ?? null),
        dddCelularPessoa: celular.ddd,
        celularPessoa: celular.numero,
        dddCelularPessoaDois: 'celularDois' in telefones ? celularDois.ddd : (anterior.dddCelularPessoaDois ?? null),
        celularPessoaDois: 'celularDois' in telefones ? celularDois.numero : (anterior.celularPessoaDois ?? null)
    };
};

/* Lista de endereços com o residencial trocado pelo do formulário (os demais tipos seguem como vieram). */
const substituirResidencial = (enderecos = [], novo) => {
    const lista = enderecos || [];
    const residencial = enderecoResidencial(lista);
    return [novo, ...lista.filter(item => item !== residencial)];
};

const montarComposicaoFamiliar = membros => (membros || []).map(membro => {
    const rendas = rendasDoMembro(membro).map(renda => ({
        ...(renda.id ? {id: renda.id} : {}),
        tpRenda: renda.tpRenda || 'P',
        ocupacao: String(renda.ocupacao || '').trim(),
        vlrRenda: parseDecimal(renda.vlrRenda) ?? 0
    }));
    const endereco = membro.enderecoComposicaoFamiliar;

    return {
        id: membro.id || null,
        descricao: membro.descricao || '',
        ocupacao: consolidarOcupacoes(rendas),
        rendaBruta: somarRendas(rendas),
        rendas,
        cpf: membro.cpf || null,
        rg: membro.rg || null,
        declaracaoIR: membro.declaracaoIR || 'N',
        dataNascimento: membro.dataNascimento || null,
        grauParentesco: membro.grauParentesco?.id ? {id: Number(membro.grauParentesco.id)} : null,
        enderecoComposicaoFamiliar: endereco ? {
            id: endereco.id || null,
            tipoEndereco: endereco.tipoEndereco?.nome || endereco.tipoEndereco,
            logradouro: endereco.logradouro,
            numero: endereco.numero,
            complemento: endereco.complemento,
            bairro: endereco.bairro,
            cep: somenteDigitos(endereco.cep) ? Number(somenteDigitos(endereco.cep)) : null,
            cidade: endereco.cidade?.id ? {id: Number(endereco.cidade.id)} : null
        } : null
    };
});

/*
 * Anexos: o do integrante já gravado vai com composicaoFamiliar.id; o de um
 * integrante novo (sem id) vai com indiceComposicaoFamiliar, a posição dele
 * na composicaoFamiliar enviada no mesmo payload.
 */
const montarAnexos = (anexos, membros = []) => (anexos || []).map(item => {
    const integrante = item.composicaoFamiliar;
    const indiceIntegrante = integrante && !integrante.id ? encontrarIndiceMembro(membros, integrante) : -1;
    return {
        id: item.id || null,
        idInscricaoPendenciaAnexo: item.idInscricaoPendenciaAnexo || null,
        descricao: item.descricao || '',
        documento: item.documento?.id ? {id: Number(item.documento.id)} : null,
        composicaoFamiliar: integrante?.id ? {id: integrante.id} : null,
        ...(indiceIntegrante >= 0 ? {indiceComposicaoFamiliar: indiceIntegrante} : {}),
        anexo: item.anexo ? {
            id: item.anexo.id || null,
            nome: item.anexo.nome,
            mimetype: item.anexo.mimetype,
            tamanho: item.anexo.tamanho ?? null,
            descricao: item.anexo.descricao || '',
            // Só o arquivo escolhido agora vai em base64; o já gravado não é reenviado.
            ...(item.anexo.arquivo ? {arquivo: item.anexo.arquivo} : {})
        } : null
    };
});

/*
 * Payload do POST/PUT a partir dos valores do formulário.
 * contexto.original: o VO carregado (edição) ou usado no pré-preenchimento —
 *   os campos que a tela não edita seguem como vieram.
 * contexto.processo: o processo de seleção escolhido ({id, descricao, programa}).
 * contexto.temResponsavelCadastrado: a pessoa já tinha responsável legal.
 * contexto.hoje: data de referência para a menoridade (testes).
 */
export const montarPayload = (valores, contexto = {}) => {
    const {original = null, processo = null, temResponsavelCadastrado = false, hoje} = contexto;
    const anterior = original || {};
    const pessoaAnterior = anterior.pessoa || {};
    const programaId = processo?.programa?.id ?? anterior.processoSeletivo?.programa?.id;
    const regras = regrasDoPrograma(programaId, processo || anterior.processoSeletivo || {});
    const dadosPessoa = valores.pessoa || {};

    const secaoResponsavel = exibirResponsavelLegal(dadosPessoa.dataNascimento, temResponsavelCadastrado, hoje);
    const tipoResponsavel = secaoResponsavel ? (valores.tipoResponsavel || null) : (anterior.tipoResponsavel ?? null);
    const comResponsavel = secaoResponsavel && exigeDadosResponsavel(tipoResponsavel);
    const emancipado = secaoResponsavel && tipoResponsavel === 'EMANCIPADO';

    const enderecoAnterior = enderecoResidencial(pessoaAnterior.enderecos);
    const enderecoPessoa = montarEnderecoPessoa(valores.endereco, enderecoAnterior, {
        telefone: dadosPessoa.telefone,
        celular: dadosPessoa.celular,
        celularDois: dadosPessoa.celularDois
    });

    // O conjunto selecionado substitui as deficiências ativas da pessoa.
    const deficiencias = (dadosPessoa.deficiencias || []).map(idDeficiencia => ({deficiencia: {id: Number(idDeficiencia)}}));

    let responsavelAtual = secaoResponsavel ? null : (pessoaAnterior.responsavelAtual ?? null);
    if (comResponsavel) {
        const relacaoAnterior = pessoaAnterior.responsavelAtual || {};
        const responsavelAnterior = relacaoAnterior.responsavel || {};
        const dados = valores.responsavel || {};
        const enderecoResponsavel = montarEnderecoPessoa(dados.endereco, enderecoResidencial(responsavelAnterior.enderecos), {
            celular: dados.celular
        });
        responsavelAtual = {
            ...relacaoAnterior,
            id: relacaoAnterior.id ?? null,
            tipoResponsavel,
            responsavel: {
                ...responsavelAnterior,
                id: responsavelAnterior.id ?? null,
                tipo: responsavelAnterior.tipo || 'FISICA',
                nome: dados.nome || '',
                cpf: dados.cpf || '',
                rg: dados.rg || '',
                dataNascimento: dados.dataNascimento || null,
                emailAtual: dados.emailAtual || '',
                enderecos: substituirResidencial(responsavelAnterior.enderecos, enderecoResponsavel)
            }
        };
    }

    let emancipada = pessoaAnterior.emancipada ?? null;
    let dataEmancipacao = pessoaAnterior.dataEmancipacao ?? null;
    if (secaoResponsavel) {
        emancipada = emancipado ? 'SIM' : 'NAO';
        dataEmancipacao = emancipado ? (dadosPessoa.dataEmancipacao || null) : null;
    }

    const pessoa = {
        ...pessoaAnterior,
        id: dadosPessoa.id ?? pessoaAnterior.id ?? null,
        tipo: pessoaAnterior.tipo || 'FISICA',
        // Pessoa nova: o CPF digitado vai para a pessoa.
        cpf: dadosPessoa.cpf || pessoaAnterior.cpf || '',
        nome: dadosPessoa.nome || '',
        nomeSocial: dadosPessoa.habilitarNomeSocial ? (dadosPessoa.nomeSocial || '') : '',
        dataNascimento: dadosPessoa.dataNascimento || null,
        sexo: dadosPessoa.sexo || null,
        nomeMae: dadosPessoa.nomeMae || '',
        nomePai: dadosPessoa.nomePai || '',
        estadoCivil: referencia(dadosPessoa.estadoCivil),
        raca: referencia(dadosPessoa.raca),
        nacionalidade: referencia(dadosPessoa.nacionalidade),
        rg: dadosPessoa.rg || '',
        orgaoRg: dadosPessoa.orgaoRg || '',
        emailAtual: dadosPessoa.emailAtual || '',
        emancipada,
        dataEmancipacao,
        nomeResponsavel: comResponsavel ? (valores.responsavel?.nome || null) : null,
        deficiencias,
        enderecos: substituirResidencial(pessoaAnterior.enderecos, enderecoPessoa),
        responsavelAtual
    };

    const usaEnem = regras.exibirEnem && textoParaBooleano(valores.utilizarEnem) === true;
    let utilizacaoNota = anterior.utilizacaoNota ?? null;
    if (regras.exibirEnem) {
        utilizacaoNota = usaEnem ? 'E' : (anterior.utilizacaoNota === 'H' ? 'H' : 'P');
    }

    const enem = valores.inscricaoEnem || {};
    const inscricaoEnem = usaEnem ? {
        id: enem.id || null,
        processoSeletivoEnem: referencia(enem.processoSeletivoEnem),
        cdEnem: somenteDigitos(enem.cdEnem) ? Number(somenteDigitos(enem.cdEnem)) : null,
        pontuacao: parseDecimal(enem.pontuacao),
        nomeArquivo: enem.nomeArquivo || null,
        mimetype: enem.mimetype || null,
        ...(enem.arquivo ? {arquivo: enem.arquivo} : {})
    } : (regras.exibirEnem ? null : (anterior.inscricaoEnem ?? null));

    let inscricaoVeterano = anterior.inscricaoVeterano ?? null;
    if (regras.exibirVeterano) {
        const veterano = valores.inscricaoVeterano || {};
        const possuiBeneficio = textoParaBooleano(veterano.possuiBeneficioNaIes);
        const participou = textoParaBooleano(veterano.participouProcessoAnterior);
        inscricaoVeterano = {
            ...(anterior.inscricaoVeterano || {}),
            possuiEnem: textoParaBooleano(veterano.possuiEnem),
            possuiBeneficioNaIes: possuiBeneficio,
            descricaoBeneficio: possuiBeneficio ? (veterano.descricaoBeneficio || '') : '',
            participouProcessoAnterior: participou,
            descricaoProcessoAnterior: participou ? (veterano.descricaoProcessoAnterior || '') : '',
            divida: textoOuNulo(veterano.divida)
        };
    }

    // Inteiro de 0 a 100 no back.
    const percentual = parseDecimal(valores.percentualBolsa);
    const percentualBolsa = percentual === null ? null : Math.round(percentual);

    const payload = {
        ...anterior,
        id: valores.id || anterior.id || null,
        processoSeletivo: processo
            ? {id: processo.id, descricao: processo.descricao, programa: processo.programa || null}
            : (anterior.processoSeletivo || referencia(valores.idProcessoSeletivo)),
        codigoAluno: regras.exibirCodigoAluno
            ? (somenteDigitos(valores.codigoAluno) ? Number(somenteDigitos(valores.codigoAluno)) : null)
            : (anterior.codigoAluno ?? null),
        pessoa,
        tipoResponsavel,
        tipoHabitacao: referencia(valores.tipoHabitacao),
        tipoMoradia: referencia(valores.tipoMoradia),
        parentesBeneficiarios: (valores.parentesBeneficiarios || []).map(item => ({
            id: item.id || null,
            nome: item.nome || '',
            bolsa: item.bolsa || '',
            grauParentesco: item.grauParentesco?.id ? {id: Number(item.grauParentesco.id)} : null
        })),
        renda: (valores.renda || []).map(item => ({
            id: item.id || null,
            tpRenda: item.tpRenda || '',
            ocupacao: item.ocupacao || '',
            vlrRenda: parseDecimal(item.vlrRenda) ?? 0
        })),
        disponibilidade: regras.exibirDisponibilidade ? parseDecimal(valores.disponibilidade) : (anterior.disponibilidade ?? null),
        vlrPensao: parseDecimal(valores.vlrPensao),
        vlrPensaoFilho: parseDecimal(valores.vlrPensaoFilho),
        declaracaoIR: valores.declaracaoIR || null,
        cadUnicoOuTransferenciaRenda: regras.exibirCadUnico
            ? textoParaBooleano(valores.cadUnicoOuTransferenciaRenda)
            : (anterior.cadUnicoOuTransferenciaRenda ?? null),
        enderecoComposicaoFamiliares: (valores.enderecoComposicaoFamiliares || []).map(item => ({
            id: item.id || null,
            tipoEndereco: item.tipoEndereco?.nome || item.tipoEndereco,
            logradouro: item.logradouro,
            numero: item.numero,
            complemento: item.complemento,
            bairro: item.bairro,
            cep: somenteDigitos(item.cep) ? Number(somenteDigitos(item.cep)) : null,
            cidade: item.cidade?.id ? {id: Number(item.cidade.id)} : null
        })),
        composicaoFamiliar: montarComposicaoFamiliar(valores.composicaoFamiliar),
        vaga: regras.exibirVaga ? referencia(valores.vaga) : (anterior.vaga ?? null),
        psInscricao: regras.exibirPsInscricao ? referencia(valores.psInscricao) : (anterior.psInscricao ?? null),
        categoria: regras.exibirCategoria ? (valores.categoria || null) : (anterior.categoria ?? null),
        sistemaCotas: regras.exibirCotas ? textoParaBooleano(valores.sistemaCotas) : (anterior.sistemaCotas ?? null),
        utilizacaoNota,
        inscricaoEnem,
        inscricaoVeterano,
        inscricaoEnsino: (valores.inscricaoEnsino || []).map(item => ({
            id: item.id || null,
            dsCurso: item.dsCurso || '',
            instituicao: item.instituicao || '',
            completo: item.completo || null,
            ensinoPublico: item.ensinoPublico || null,
            dataConclusao: item.completo === 'S' ? (item.dataConclusao || null) : null,
            formacao: item.formacao?.id ? {id: Number(item.formacao.id)} : null
        })),
        motivo: valores.motivo || '',
        tipoBolsa: textoOuNulo(valores.tipoBolsa),
        percentualBolsa,
        observacao: valores.observacao || '',
        inscricaoAnexos: montarAnexos(valores.inscricaoAnexos, valores.composicaoFamiliar),
        calendarios: (valores.calendarios || []).map(item => ({
            id: item.id || null,
            idTipo: item.idTipo === null || item.idTipo === undefined ? null : Number(item.idTipo),
            descricaoTipo: item.descricaoTipo || '',
            dataInicio: item.dataInicio,
            dataFim: item.dataFim
        })),
        idAtendente: valores.idAtendente === '' || valores.idAtendente === null || valores.idAtendente === undefined
            ? null
            : Number(valores.idAtendente)
    };

    // Só leitura: calculado pelo back.
    delete payload.ultimaAlteracao;

    return payload;
};
